import type {ReactElement} from 'react';
import React, {useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import type {EmojiClickData} from 'emoji-picker-react';
import EmojiPicker from 'emoji-picker-react';
import useOutsideClick from '@Hooks/useOutsideClick';

const EMOJI_PICKER_HEIGHT = 400;
const EMOJI_PICKER_WIDTH = 280;

interface DescriptionRteEmojiActionProps {
  onOpen: () => void;
  onSelect: (emoji: string) => void;
}

export function DescriptionRteEmojiAction({onOpen, onSelect}: DescriptionRteEmojiActionProps): ReactElement {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const popupContainerRef = useRef<HTMLDivElement | null>(null);

  useOutsideClick(popupContainerRef, (): void => {
    setIsOpen(false);
  });

  const popupPosition = useMemo((): {top: number; left: number} | null => {
    if (!isOpen || !buttonRef.current) {
      return null;
    }

    const toolbarEl = buttonRef.current.closest('.gjs-rte-toolbar');
    const anchorRect = (toolbarEl ?? buttonRef.current).getBoundingClientRect();

    return {top: anchorRect.bottom, left: anchorRect.left};
  }, [isOpen]);

  const onIconClick = (event: React.MouseEvent): void => {
    event.stopPropagation();
    setIsOpen((prev) => !prev);
  };

  const onEmojiSelected = (emojiData: EmojiClickData): void => {
    onSelect(emojiData.emoji);
    setIsOpen(false);
  };

  const renderPopup = (): ReactElement | null => {
    if (!isOpen || !popupPosition) {
      return null;
    }

    return createPortal(
      <div
        ref={(node): void => {
          popupContainerRef.current = node;
        }}
        className="pmw-fm-rte-emoji-popup"
        style={{position: 'fixed', top: popupPosition.top + 1, left: popupPosition.left, zIndex: 10000}}
      >
        <EmojiPicker width={EMOJI_PICKER_WIDTH} height={EMOJI_PICKER_HEIGHT} onEmojiClick={onEmojiSelected} lazyLoadEmojis />
      </div>,
      document.body
    );
  };

  return (
    <>
      <div
        ref={buttonRef}
        className="pmw-fm-rte-emoji-icon-wrap"
        onMouseDown={(e): void => {
          onOpen();
          e.preventDefault();
        }}
        onClick={onIconClick}
      >
        <i className="pmw-icon icon-sentiment-very-satisfied size-icon-16" />
      </div>
      {renderPopup()}
    </>
  );
}
